import { ProductAttribute } from "@wttb/product-configurator";
import { useMemo } from "react";
import { IoCloseCircle } from "react-icons/io5";

import { ChoiceButton } from "@/common/components/form/choice-button";
import { FlagType } from "@/features/product/components/product-attributes/product-attributes-renderer";
import { useStoreProduct } from "@/features/product/contexts/product-context";

export type MultipleOption = {
  id: string;
  name: string;
  value: string | number;
  flags?: FlagType[];
  disabled: boolean;
  isIncompatible?: boolean;
};

interface MultipleChoiceButtonSelectorTypes {
  label: string;
  options: MultipleOption[];
  attribute: ProductAttribute;
  onChange: (value: string[]) => void;
}

interface SelectionInfoTypes {
  label: string;
  selectedValues: (string | number)[];
  onRemove: (valueId: string) => void;
  options: MultipleOption[];
}

const getNameByValue = (options: MultipleOption[], value: string | number) => {
  const result = options?.find((option) => option.value === value);
  return result?.name;
};

const SelectionInfo: React.FC<SelectionInfoTypes> = ({
  selectedValues,
  label,
  onRemove,
  options,
}) => {
  const handleRemove = (value: string | number) => {
    const valueId = options.find((option) => option.value === value)!.id as string;
    onRemove(valueId);
  };

  return (
    <>
      {selectedValues.length > 0 && (
        <div className="my-4 text-sm">You have added following {label.toLocaleLowerCase()}</div>
      )}

      {selectedValues.map((value) => {
        return (
          <div key={value} className="bg-berry-10 my-2 flex justify-between py-3 pl-4 pr-2">
            <span className="text-base">{getNameByValue(options, value)}</span>
            <span className="cursor-pointer" onClick={() => handleRemove(value)}>
              <IoCloseCircle color="#5b6771" size={24} />
            </span>
          </div>
        );
      })}
    </>
  );
};

export function MultipleChoiceButtonSelector({
  label,
  options,
  onChange,
  attribute,
}: MultipleChoiceButtonSelectorTypes) {
  const { config } = useStoreProduct();
  const values = (config![attribute.key] ?? []) as (string | number)[];

  const valueIds = useMemo(() => {
    return values
      .map((value) => {
        const option = options.find((option) => option.value === value);
        return option?.id;
      })
      .filter(Boolean) as string[];
  }, [values, options]);

  const addSelection = (valueId: string) => {
    const updatedSelection = Array.from(new Set([...valueIds, valueId]));
    onChange(updatedSelection);
  };

  const removeSelection = (valueId: string) => {
    const updatedSelection = valueIds.filter((id) => id !== valueId);
    onChange(updatedSelection);
  };

  if (!options?.length && values.length > 0) {
    onChange([]);
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-6 text-center">
        {options?.map((option: MultipleOption) => (
          <ChoiceButton
            {...option}
            onInputChange={addSelection}
            key={option.id}
            isChecked={values.includes(option.value)}
            type="checkbox"
            disabled={option.disabled}
            flags={option?.flags}
          />
        ))}
      </div>
      <SelectionInfo
        selectedValues={values}
        label={label}
        onRemove={removeSelection}
        options={options}
      />
    </>
  );
}
